import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/contexts/UserContext';
import { User, Mail, Lock, UserPlus, ArrowLeft } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

const CompleteSignupPage = () => {
  const navigate = useNavigate();
  const { user, login } = useUser();
  const [coachInfo, setCoachInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get coach info from session storage
    const pendingInfo = sessionStorage.getItem('pending_coach_info');
    if (pendingInfo) {
      setCoachInfo(JSON.parse(pendingInfo));
    } else {
      // No coach info, redirect back to welcome
      navigate('/', { replace: true });
    }

    // Auto-redirect if already logged in
    if (user) {
      if (user.role === 'master_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.role === 'coach') {
        navigate('/coach/dashboard', { replace: true });
      } else {
        navigate('/client/hub', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!coachInfo) {
      setError('Coach information missing. Please start over.');
      return;
    }

    setLoading(true);

    // Use XMLHttpRequest to avoid fetch interceptor issues
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/auth/register`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      setLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 400) {
          setError(data.detail || 'Registration failed');
          return;
        }
        
        if (!data.user) {
          setError('Invalid response from server');
          return;
        }
        
        // Clear the pending coach info
        sessionStorage.removeItem('pending_coach_info');
        
        // Login and redirect
        login(data.user, data.access_token);
        navigate('/client/hub');
      } catch (parseError) {
        setError('Registration failed. Please try again.');
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Network error. Please try again.');
    };
    
    xhr.send(JSON.stringify({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: 'client',
      coach_name: coachInfo.coachName,
      coach_id_key: coachInfo.coachIdKey
    }));
  };

  if (!coachInfo) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-ivory-mist flex items-center justify-center p-4 pb-20">
      <div className="max-w-md w-full">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-obsidian-night mb-2">
            GNTL STRNGTH
          </h1>
          <p className="text-shadow-pine font-body text-sm sm:text-base">
            Complete your registration
          </p>
        </div>

        <Card className="bg-white border-burnt-terra/20">
          <CardHeader className="pb-4">
            <CardTitle className="font-display text-obsidian-night text-center text-lg">
              Create Your Account
            </CardTitle>
            <CardDescription className="text-shadow-pine font-body text-center text-sm">
              Coach: {coachInfo.coachName}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-obsidian-night font-body text-sm">
                  Full Name <span className="text-burnt-terra">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-obsidian-night font-body text-sm">
                  Email <span className="text-burnt-terra">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-obsidian-night font-body text-sm">
                  Password <span className="text-burnt-terra">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a password (min 6 characters)"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-obsidian-night font-body text-sm">
                  Confirm Password <span className="text-burnt-terra">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-sm font-body text-center bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
                data-testid="submit-registration-btn"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
                <UserPlus className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-shadow-pine/20">
              <Link 
                to="/" 
                className="flex items-center justify-center text-sm text-shadow-pine hover:text-burnt-terra"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to start
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
      <BrandMark />
    </div>
  );
};

export default CompleteSignupPage;
